import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, Car, Sparkles, Check, Shield, Clock, Users, Phone, MessageCircle } from 'lucide-react';

const HERO_IMAGE = "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=1920&q=80";
const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_47615226-8be8-492e-9b64-c74bd12fbe55/artifacts/a0f8zaxp_02_MA_Africa_Detailing_Full_Colour_Transparent.webp";

// Service category images
const MECHANICAL_IMG = "https://images.unsplash.com/photo-1676018366904-c083ed678e60?w=800&q=80";
const PANEL_BEATING_IMG = "https://images.unsplash.com/photo-1786489785858-b64715400a37?w=800&q=80";
const DETAILING_IMG = "https://images.unsplash.com/photo-1620584898989-d39f7f9ed1b7?w=800&q=80";

// Service categories
const serviceCategories = [
  {
    id: 'mechanical',
    title: 'Mechanical',
    subtitle: 'Repairs & Maintenance',
    description: 'Professional maintenance, diagnostics and mechanical repairs to keep your vehicle safe, reliable and performing properly.',
    image: MECHANICAL_IMG,
    icon: Wrench,
    services: ['Vehicle Diagnostics', 'Major & Minor Services', 'Engine Repairs', 'Brake Repairs', 'Suspension & Steering', 'Electrical Repairs'],
    cta: 'Book Mechanical Service',
    link: '/booking?category=mechanical',
    color: '#C41E3A'
  },
  {
    id: 'panel-beating',
    title: 'Panel Beating',
    subtitle: 'Body & Paint Repair',
    description: 'Professional accident damage, body repair, paintwork and cosmetic restoration.',
    image: PANEL_BEATING_IMG,
    icon: Car,
    services: ['Accident Damage Repair', 'Dent & Scratch Repair', 'Spray Painting', 'Bumper Repair', 'Colour Matching', 'Insurance Assessments'],
    cta: 'Request Body Repair Quote',
    link: '/repair-quote',
    color: '#424245'
  },
  {
    id: 'detailing',
    title: 'Detailing',
    subtitle: 'From R350',
    description: 'Professional interior and exterior detailing designed to restore, protect and maintain your vehicle.',
    image: DETAILING_IMG,
    icon: Sparkles,
    services: ['Express Wash from R350', 'Full Valet from R800', 'Prestige Detail from R1,200', 'Paint Correction', 'Ceramic Coating', 'Headlight Restoration'],
    cta: 'Book Detailing',
    link: '/services',
    color: '#C41E3A'
  }
];

const trustFeatures = [
  {
    icon: Wrench,
    title: 'Complete Service Centre',
    description: 'Mechanical, body repair and detailing under one roof.'
  },
  {
    icon: Shield,
    title: 'Quality Workmanship',
    description: 'Professional standards on every job.'
  },
  {
    icon: Clock,
    title: 'Transparent Process',
    description: 'Clear communication and approved quotes before work begins.'
  },
  {
    icon: Users,
    title: 'Customer First',
    description: 'Your vehicle is in trusted hands.'
  }
];

const processSteps = [
  { step: '01', title: 'Choose', description: 'Select mechanical, body repair or detailing.' },
  { step: '02', title: 'Book or Request Quote', description: 'Book available services or submit for assessment.' },
  { step: '03', title: 'We Inspect & Work', description: 'Our team completes the approved work.' },
  { step: '04', title: 'Quality Check', description: 'Final inspection before handover.' },
  { step: '05', title: 'Handover', description: 'Review and collect your vehicle.' }
];

export default function Home() {
  const whatsappNumber = '27760784050';
  const whatsappMessage = encodeURIComponent('Hi MA Africa, I would like to enquire about your services.');

  return (
    <main data-testid="home-page" className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE}
            alt="Professional automotive workshop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 pt-24 pb-16">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <img 
                src={LOGO_URL} 
                alt="MA Africa Fix & Detail" 
                className="h-16 md:h-20 mb-6"
              />
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-4">
                <span className="text-[#C41E3A]">FIX.</span> RESTORE. <span className="text-[#C41E3A]">DETAIL.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl">
                Mechanical repairs, panel beating, cosmetic restoration and professional vehicle detailing — all under one roof.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/booking"
                  data-testid="hero-book-service"
                  className="inline-flex items-center justify-center gap-2 bg-[#C41E3A] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#a01830] transition-all text-base"
                >
                  Book a Service
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/repair-quote"
                  data-testid="hero-repair-quote"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur text-white border border-white/30 px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all text-base"
                >
                  Request a Repair Quote
                </Link>
              </div>

              {/* WhatsApp CTA */}
              <a
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-white mt-6 text-sm transition-colors"
                data-testid="hero-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                Or WhatsApp us directly
              </a>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Service Categories */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-4">
              Complete Automotive Care
            </h2>
            <p className="text-base md:text-lg text-[#86868b] max-w-2xl mx-auto">
              We don't just clean cars. We fix, restore and bring them back to life.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {serviceCategories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-[#C41E3A]/30 hover:shadow-xl transition-all duration-300"
                data-testid={`category-${category.id}`}
              >
                {/* Image */}
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img 
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center">
                      <category.icon className="w-6 h-6 text-[#C41E3A]" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-xs text-[#C41E3A] font-semibold uppercase tracking-wider mb-1">
                    {category.subtitle}
                  </p>
                  <h3 className="text-xl font-bold text-[#1d1d1f] mb-3">
                    {category.title}
                  </h3>
                  <p className="text-sm text-[#86868b] mb-4 leading-relaxed">
                    {category.description}
                  </p>

                  {/* Services list */}
                  <ul className="space-y-2 mb-6">
                    {category.services.slice(0, 4).map((service) => (
                      <li key={service} className="flex items-center gap-2 text-sm text-[#424245]">
                        <Check className="w-4 h-4 text-[#C41E3A] flex-shrink-0" />
                        {service}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to={category.link}
                    className="inline-flex items-center justify-center w-full gap-2 bg-[#1d1d1f] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#C41E3A] transition-colors text-sm"
                    data-testid={`cta-${category.id}`}
                  >
                    {category.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-[#f5f5f7]">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-4">
              How MA Africa Works
            </h2>
            <p className="text-base text-[#86868b]">
              From booking to handover — a clear, professional process.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
            {processSteps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-full bg-[#C41E3A] text-white flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  {item.step}
                </div>
                <h3 className="text-base font-semibold text-[#1d1d1f] mb-1">{item.title}</h3>
                <p className="text-xs text-[#86868b] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why MA Africa */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-4">
              Why MA Africa?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`trust-feature-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#f5f5f7] flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-[#C41E3A]" />
                </div>
                <h3 className="text-base font-semibold text-[#1d1d1f] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#86868b]">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* One Vehicle Promise */}
      <section className="py-16 md:py-24 bg-[#1d1d1f]">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
              One Vehicle. One Team. <span className="text-[#C41E3A]">Complete Care.</span>
            </h2>
            <p className="text-base md:text-lg text-gray-400 mb-8 max-w-xl mx-auto leading-relaxed">
              Mechanical work, accident and body repair, and professional detailing — working together as one MA Africa ecosystem.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                data-testid="cta-book-now"
                className="inline-flex items-center justify-center gap-2 bg-[#C41E3A] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#a01830] transition-colors"
              >
                Book Now
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1fb855] transition-colors"
                data-testid="cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <a
        href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        data-testid="floating-whatsapp"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </main>
  );
}
